const {
    BufferGeometry,
    CircleGeometry,
    CylinderGeometry, DoubleSide,
    Group,
    Line, LineBasicMaterial,
    Mesh,
    MeshPhongMaterial,
    SphereGeometry,
    Vector3, TransformControls
} = THREE;
import {mapState} from "vuex";

export default {
    name: "RigidJoint",

    data() {
        return {
            Callbacks: {
                VOLUME_FOR_POINT: {
                    running: false,
                    callback: this.pickVolumeForPoint
                },
                POINT_FOR_POINT: {
                    running: false,
                    callback: this.pickPointForPoint
                },
                MEASUREMENT_FOR_POINT: {
                    running: false,
                    callback: this.pickMeasurementForPoint
                },
                VOLUME_FOR_TARGET: {
                    running: false,
                    callback: this.pickVolumeForTarget
                },
                POINT_FOR_TARGET: {
                    running: false,
                    callback: this.pickPointForTarget
                },
                MEASUREMENT_FOR_TARGET: {
                    running: false,
                    callback: this.pickMeasurementForTarget
                }
            },
            flags: {
                positionControlEnabled: false,
                endControlEnabled: false
            }
        }
    },

    props: {
        parent: {
            required: true
        },
        volumeMesh: {
            required: true
        },
        joint: {
            required: true,
        },
        chain: {
            required: true
        },
        disabled: {
            type: Object
        }
    },

    computed: {
        joints: {
            get() {
                return this.chain.joints;
            }
        }
    },

    created() {
        this.calculateParameters();
        this.view = this.createView();
        this.parent.add(this.view.container);
        this.updateView();
    },

    destroyed() {
        this.parent.remove(this.view.container);
        this.resetAllCallbacks();
    },

    watch: {
        'joint.volumes': {
            handler() {
                this.parent.remove(this.view.container);
                this.resetAllCallbacks();

                this.calculateParameters();
                this.view = this.createView();
                this.parent.add(this.view.container);
                this.updateView();
            }
        },

        'joint.pointOfAction': {
            handler () {
                this.updateView();
            },
            deep: true
        },

        'joint.pointOfControl': {
            handler () {
                this.updateView();
            },
            deep: true
        },
    },

    methods: {
        calculateParameters() {
            this.sizeFactor = new THREE.Vector3();
            getModelBoundingBox().getSize(this.sizeFactor);
            this.sizeFactor = this.sizeFactor.length() / 2.667;

            this.endingCenter = new Vector3();
            let boundingBox = getCurrentBoundingBoxOfVolumesInWorld(this.joint.volumes);
            boundingBox.getCenter(this.endingCenter);

            this.joint.pointOfControl.copy(this.endingCenter);

            let axes = getPrincipleComponents(this.joint.volumes);

            if (!axes) {
                this.noPCA = true;

                this.joint.pointOfAction.addVectors(boundingBox.min, new Vector3(boundingBox.max.x, boundingBox.max.y, boundingBox.min.z)).multiplyScalar(0.5);
            } else {
                this.noPCA = false;
                let majorAxis = axes[0];

                this.joint.pointOfAction.addVectors(this.endingCenter, majorAxis.clone().multiplyScalar(0.4));
            }

            this.joint.initialDirection.subVectors(this.endingCenter, this.joint.pointOfAction);
        },

        updateView() {
            this.joint.initialDirection.subVectors(this.joint.pointOfControl, this.joint.pointOfAction);

            this.view.target.position.copy(this.joint.pointOfAction);
            this.view.endTarget.position.copy(this.joint.pointOfControl);

            this.view.bone.position.copy(this.joint.pointOfAction);
            this.view.bone.lookAt(this.joint.pointOfControl);
            this.view.boneBody.scale.y = new Vector3().subVectors(this.joint.pointOfAction, this.joint.pointOfControl).length();
            this.view.boneBody.position.z = new Vector3().subVectors(this.joint.pointOfAction, this.joint.pointOfControl).length() / 2;
        },

        createView() {
            let data = {};

            data.container = new Group();

            data.bone = new Group();
            data.bone.lookAt(0, -1, 0);
            data.boneBody = new Mesh(new CylinderGeometry(0.01 * this.sizeFactor, 0.01 * this.sizeFactor, 1, 25, 25), new MeshPhongMaterial({color: 0x0000ff}))

            data.bone.attach(data.boneBody);
            data.container.add(data.bone);

            data.target = new Group();
            data.container.add(data.target);

            data.endTarget = new Group();
            data.container.add(data.endTarget);

            data.control = new TransformControls(iq3Camera.camera, iq3Renderer.getRenderer().domElement);
            data.control.setSize(1);

            data.endControl = new TransformControls(iq3Camera.camera, iq3Renderer.getRenderer().domElement);
            data.endControl.setSize(1);

            data.control.addEventListener("pointerdown", () => {
                VR.mouseControls.enabled = false;
            });

            data.control.addEventListener("pointerup", () => {
                VR.mouseControls.enabled = true;
            });

            data.control.addEventListener("objectChange", () => {
                this.joint.pointOfAction.copy(data.target.position);
            });

            data.endControl.addEventListener("pointerdown", () => {
                VR.mouseControls.enabled = false;
            });

            data.endControl.addEventListener("pointerup", () => {
                VR.mouseControls.enabled = true;
            });

            data.endControl.addEventListener("objectChange", () => {
                this.joint.pointOfControl.copy(data.endTarget.position);
            });

            data.container.add(data.control);
            data.container.add(data.endControl);

            return data;
        },

        resetAllCallbacks() {
            for (let type in this.Callbacks) {
                if (this.Callbacks[type].running) {
                    this.setPickingCallback(this.Callbacks[type]);
                }
            }

            if (this.flags.positionControlEnabled) {
                this.togglePositionControl();
            }

            if (this.flags.endControlEnabled) {
                this.toggleEndControl();
            }
        },

        setPickingCallback(data) {
            data.running = !data.running;

            wv.mouseClickCallbackHandler.handleCallback({
                callback:  data.callback
            }, data.running);
        },

        pickVolumeForPoint(event) {
            for (const volume of this.volumeMesh.volumeList) {
                flipAlphaValueOfThisVolume(volume.branchID);
            }

            let pick = event.pickingData;

            for (const volume of this.volumeMesh.volumeList) {
                flipAlphaValueOfThisVolume(volume.branchID);
            }

            if (pick.volume) {
                getCurrentBoundingBoxOfVolumesInWorld(pick.volume.branchID).getCenter(this.joint.pointOfAction);
                this.setPickingCallback(this.Callbacks.VOLUME_FOR_POINT);
            }
        },

        pickPointForPoint(event) {
            for (const volume of this.volumeMesh.volumeList) {
                flipAlphaValueOfThisVolume(volume.branchID);
            }

            let pick = event.pickingData;

            for (const volume of this.volumeMesh.volumeList) {
                flipAlphaValueOfThisVolume(volume.branchID);
            }

            if (pick.volume) {
                this.joint.pointOfAction.copy(pick.point);
                this.setPickingCallback(this.Callbacks.POINT_FOR_POINT);
            }
        },

        pickMeasurementForPoint(event) {
            let measurement = measurementController.getMeasurementFromMouseClick(event);

            if (!measurement) {
                return;
            }

            let point1, point2, point3, circleData;
            let data = wv.splitServerMessage(measurement.data);

            switch (measurement.type) {
                case MeasurementType.LINE_BETWEEN_POINT:
                    point1 = getPointWorldPosition(new Vector3(parseFloat(data[1]), parseFloat(data[2]), parseFloat(data[3])), data[0]);
                    point2 = getPointWorldPosition(new Vector3(parseFloat(data[5]), parseFloat(data[6]), parseFloat(data[7])), data[4]);

                    this.joint.pointOfAction.addVectors(point1, point2).multiplyScalar(0.5);
                    break;
                case MeasurementType.LINE_BETWEEN_PLANE:
                    point1 = getPointWorldPosition(new Vector3(parseFloat(data[1]), parseFloat(data[2]), parseFloat(data[3])), data[0]);
                    point2 = getPointWorldPosition(new Vector3(parseFloat(data[8]), parseFloat(data[9]), parseFloat(data[10])), data[7]);

                    this.joint.pointOfAction.addVectors(point1, point2).multiplyScalar(0.5);
                    break;
                case MeasurementType.CIRCLE_FROM_POINT:
                    point1 = getPointWorldPosition(new Vector3(parseFloat(data[1]), parseFloat(data[2]), parseFloat(data[3])), data[0]);
                    point2 = getPointWorldPosition(new Vector3(parseFloat(data[5]), parseFloat(data[6]), parseFloat(data[7])), data[4]);
                    point3 = getPointWorldPosition(new Vector3(parseFloat(data[9]), parseFloat(data[10]), parseFloat(data[11])), data[8]);
                    circleData = getCircleDataFromThreePoints([point1, point2, point3]);

                    this.joint.pointOfAction.copy(circleData.circleCenter);
                    break;
            }

            this.setPickingCallback(this.Callbacks.MEASUREMENT_FOR_POINT);
        },

        pickVolumeForTarget(event) {
            for (const volume of this.volumeMesh.volumeList) {
                flipAlphaValueOfThisVolume(volume.branchID);
            }

            let pick = event.pickingData;

            for (const volume of this.volumeMesh.volumeList) {
                flipAlphaValueOfThisVolume(volume.branchID);
            }

            if (pick.volume) {
                getCurrentBoundingBoxOfVolumesInWorld(pick.volume.branchID).getCenter(this.joint.pointOfControl);
                this.setPickingCallback(this.Callbacks.VOLUME_FOR_TARGET);
            }
        },

        pickPointForTarget(event) {
            for (const volume of this.volumeMesh.volumeList) {
                flipAlphaValueOfThisVolume(volume.branchID);
            }

            let pick = event.pickingData;

            for (const volume of this.volumeMesh.volumeList) {
                flipAlphaValueOfThisVolume(volume.branchID);
            }

            if (pick.volume) {
                this.joint.pointOfControl.copy(pick.point);
                this.setPickingCallback(this.Callbacks.POINT_FOR_TARGET);
            }
        },

        pickMeasurementForTarget(event) {
            let measurement = measurementController.getMeasurementFromMouseClick(event);

            if (!measurement) {
                return;
            }

            let point1, point2, point3, circleData;
            let data = wv.splitServerMessage(measurement.data);

            switch (measurement.type) {
                case MeasurementType.LINE_BETWEEN_POINT:
                    point1 = getPointWorldPosition(new Vector3(parseFloat(data[1]), parseFloat(data[2]), parseFloat(data[3])), data[0]);
                    point2 = getPointWorldPosition(new Vector3(parseFloat(data[5]), parseFloat(data[6]), parseFloat(data[7])), data[4]);

                    this.joint.pointOfControl.addVectors(point1, point2).multiplyScalar(0.5);
                    break;
                case MeasurementType.LINE_BETWEEN_PLANE:
                    point1 = getPointWorldPosition(new Vector3(parseFloat(data[1]), parseFloat(data[2]), parseFloat(data[3])), data[0]);
                    point2 = getPointWorldPosition(new Vector3(parseFloat(data[8]), parseFloat(data[9]), parseFloat(data[10])), data[7]);

                    this.joint.pointOfControl.addVectors(point1, point2).multiplyScalar(0.5);
                    break;
                case MeasurementType.CIRCLE_FROM_POINT:
                    point1 = getPointWorldPosition(new Vector3(parseFloat(data[1]), parseFloat(data[2]), parseFloat(data[3])), data[0]);
                    point2 = getPointWorldPosition(new Vector3(parseFloat(data[5]), parseFloat(data[6]), parseFloat(data[7])), data[4]);
                    point3 = getPointWorldPosition(new Vector3(parseFloat(data[9]), parseFloat(data[10]), parseFloat(data[11])), data[8]);
                    circleData = getCircleDataFromThreePoints([point1, point2, point3]);

                    this.joint.pointOfControl.copy(circleData.circleCenter);
                    break;
            }

            this.setPickingCallback(this.Callbacks.MEASUREMENT_FOR_TARGET);
        },

        togglePositionControl() {
            this.flags.positionControlEnabled = !this.flags.positionControlEnabled;

            if (this.flags.positionControlEnabled) {
                this.view.control.attach(this.view.target);
            } else {
                this.view.control.detach();
            }
        },

        toggleEndControl() {
            this.flags.endControlEnabled = !this.flags.endControlEnabled;

            if (this.flags.endControlEnabled) {
                this.view.endControl.attach(this.view.endTarget);
            } else {
                this.view.endControl.detach();
            }
        }
    }
}
